import { Layout } from 'components/Layout/Layout';
import { Loader } from 'components/Loader/Loader';
import { UgPager } from 'components/UgPager/UgPager';
import {
  GamingHistoryMobile,
  MobileGamingHistoryRow,
} from 'components/mobile/GamingHistory/GamingHistory';
import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import { UserContext } from 'context/userContext';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AccountStatementRow,
  getAccountStatement,
} from 'services/api/accountStatementService';
import styles from '../styles/pages/GamingHistory.module.css';
import filterStyles from 'components/ClosedBetsFilter/ClosedBetsFilter.module.css';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 15;

function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDateInput(v: string, endOfDay = false): Date | undefined {
  if (!v) return undefined;
  const [y, m, d] = v.split('-').map((x) => Number.parseInt(x, 10));
  if (!y || !m || !d) return undefined;
  const dt = new Date(
    y,
    m - 1,
    d,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0
  );
  return Number.isNaN(dt.getTime()) ? undefined : dt;
}

function extractProvider(casinoGameName?: string): string {
  if (!casinoGameName) return '';
  const parts = casinoGameName.split('/');
  return parts[0]?.trim() ?? '';
}

function extractGameName(casinoGameName?: string): string {
  if (!casinoGameName) return '-';
  const parts = casinoGameName.split('/');
  if (parts.length <= 1) return casinoGameName;
  return parts.slice(1).join('/').trim() || casinoGameName;
}

export const GamingHistory = () => {
  const { loggedUser } = useContext(UserContext);
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('3');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>(
    toDateInputValue(new Date())
  );
  const [endDate, setEndDate] = useState<string>(toDateInputValue(new Date()));
  const [rows, setRows] = useState<AccountStatementRow[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: (() => {
      const now = new Date();
      now.setDate(now.getDate() - 3);
      now.setHours(0, 0, 0, 0);
      return now;
    })(),
    toDate: (() => {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      return now;
    })(),
    provider: 'all',
  });

  useEffect(() => {
    if (!loggedUser) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await getAccountStatement(loggedUser, page, PAGE_SIZE, {
        fromDate: appliedFilters.fromDate,
        toDate: appliedFilters.toDate,
        casinoOnly: true,
      });
      if (cancelled) return;
      const apiRows = res?.rows ?? [];
      const filteredRows =
        appliedFilters.provider === 'all'
          ? apiRows
          : apiRows.filter(
              (row) =>
                extractProvider(row.casino_game_name).toLowerCase() ===
                appliedFilters.provider.toLowerCase()
            );
      setRows(filteredRows);
      setTotalPages(res?.total_pages ?? 1);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loggedUser, page, appliedFilters]);

  const providerOptions = useMemo(() => {
    const providers = new Set<string>();
    rows.forEach((row) => {
      const provider = extractProvider(row.casino_game_name);
      if (provider) providers.add(provider);
    });
    return Array.from(providers).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  // Every change of period, dates or provider applies at once - no "Show"
  // button. The first render is skipped: the list already loads 3 days.
  const filtersReady = useRef(false);
  useEffect(() => {
    if (!filtersReady.current) {
      filtersReady.current = true;
      return;
    }
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = new Date();
    to.setHours(23, 59, 59, 999);
    if (selectedPeriod === 'custom_date') {
      const customFrom = parseDateInput(startDate, false);
      const customTo = parseDateInput(endDate, true);
      // A date still being typed waits until it is whole.
      if (!customFrom || !customTo) return;
      setAppliedFilters({
        fromDate: customFrom,
        toDate: customTo,
        provider: providerFilter,
      });
    } else {
      if (selectedPeriod !== '0') {
        from.setDate(from.getDate() - Number.parseInt(selectedPeriod, 10));
      }
      setAppliedFilters({
        fromDate: from,
        toDate: to,
        provider: providerFilter,
      });
    }
    setPage(1);
  }, [selectedPeriod, startDate, endDate, providerFilter]);

  if (!loggedUser) {
    navigate('/login');
    return null;
  }

  const content = (
    <div className={styles.wrapper}>
      <h2 className={styles.title}>
        {t('gaming_history_label').toUpperCase()}
      </h2>

      <div className={filterStyles.closedBetsFormWrapper}>
        <div className={filterStyles.formInputValue}>
          <span>Games provider</span>
          <select
            value={providerFilter}
            onChange={(event) => setProviderFilter(event.target.value)}
          >
            <option value="all">Select games provider</option>
            {providerOptions.map((provider) => (
              <option key={provider} value={provider}>
                {provider}
              </option>
            ))}
          </select>
        </div>
        <div className={filterStyles.formInputValue}>
          <span>{t('select_period_label')}</span>
          <select
            value={selectedPeriod}
            onChange={(event) => setSelectedPeriod(event.target.value)}
          >
            <option value="0">{t('today_label')}</option>
            <option value="3">{t('last_3_days_label')}</option>
            <option value="7">{t('last_7_days_label')}</option>
            <option value="14">{t('last_14_days_label')}</option>
            <option value="30">{t('last_30_days_label')}</option>
            <option value="60">{t('last_60_days_label')}</option>
            <option value="custom_date">{t('another_period_label')}</option>
          </select>
        </div>
        {selectedPeriod === 'custom_date' && (
          <>
            <div className={filterStyles.formInputValue}>
              <span>{t('start_date_label')}</span>
              <input
                className={styles.filterDate}
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className={filterStyles.formInputValue}>
              <span>{t('end_date_label')}</span>
              <input
                className={styles.filterDate}
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </>
        )}
      </div>

      <div className={styles.tableWrap}>
        <div className={styles.headerRow}>
          <div>Game round</div>
          <div>Date</div>
          <div>Game</div>
          <div>Stake</div>
          <div>Gain</div>
        </div>
        {loading && <Loader />}
        {!loading &&
          rows.map((row) => (
            <div key={row.id} className={styles.row}>
              <div>{row.id}</div>
              <div>{row.timestamp}</div>
              <div>{extractGameName(row.casino_game_name)}</div>
              <div>
                {row.amount < 0 ? Math.abs(row.amount).toFixed(2) : '-'}
              </div>
              <div>{row.amount > 0 ? row.amount.toFixed(2) : '-'}</div>
            </div>
          ))}
      </div>

      <div className={styles.pagerRow}>
        <UgPager
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </div>
  );

  const mobileRows: MobileGamingHistoryRow[] = rows.map((row) => ({
    id: row.id,
    timestamp: row.timestamp,
    gameName: extractGameName(row.casino_game_name),
    stake: row.amount < 0 ? Math.abs(row.amount).toFixed(2) : '-',
    gain: row.amount > 0 ? row.amount.toFixed(2) : '-',
  }));

  if (isMobile) {
    return (
      <MobileLayout pageTitle="gaming_history_label">
        <GamingHistoryMobile
          rows={mobileRows}
          loading={loading}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          selectedPeriod={selectedPeriod}
          onSelectedPeriodChange={setSelectedPeriod}
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          providerFilter={providerFilter}
          providerOptions={providerOptions}
          onProviderFilterChange={setProviderFilter}
        />
      </MobileLayout>
    );
  }
  return <Layout whiteBg>{content}</Layout>;
};
